import React from 'react';

const ElevationProfile = ({ minAlt, maxAlt, elevGain, elevLoss, distance }) => {
  if (!minAlt && !maxAlt) return null;

  const width = 320;
  const height = 120;
  const padding = { top: 10, right: 10, bottom: 25, left: 45 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const altRange = maxAlt - minAlt || 1;
  const startAlt = minAlt + elevGain - (maxAlt - minAlt) > elevLoss 
    ? minAlt 
    : elevGain > elevLoss ? minAlt : maxAlt;
  const endAlt = startAlt + elevGain - elevLoss;
  const clampedEnd = Math.max(minAlt, Math.min(maxAlt, endAlt));

  // Determine how many intermediate ups/downs based on cumulative gain vs net change
  const netChange = Math.abs(elevGain - elevLoss);
  const totalVertical = elevGain + elevLoss;
  const undulation = totalVertical > 0 ? (totalVertical - netChange) / totalVertical : 0;
  
  // Generate realistic profile using segments with variation
  const numSegments = 80;
  const seed = (minAlt * 7 + maxAlt * 13 + distance * 3) % 1000;
  
  // Pseudo-random based on seed for consistent results
  const pseudoRandom = (i) => {
    const x = Math.sin(seed + i * 127.1) * 43758.5453;
    return x - Math.floor(x);
  };

  // Build altitude array distributing gain/loss realistically
  const altitudes = [startAlt > minAlt ? maxAlt : minAlt];
  let remainingGain = elevGain;
  let remainingLoss = elevLoss;
  
  // Create waypoints with intermediate peaks/valleys
  const numWaypoints = Math.min(8, Math.max(3, Math.round(undulation * 6) + 2));
  const waypoints = [];
  
  for (let i = 1; i <= numWaypoints; i++) {
    const progress = i / numWaypoints;
    const r = pseudoRandom(i);
    
    if (elevGain > elevLoss * 1.5) {
      // Predominantly uphill
      const baseAlt = minAlt + (maxAlt - minAlt) * Math.pow(progress, 0.6);
      const variation = undulation * altRange * 0.15 * Math.sin(r * Math.PI * 2);
      waypoints.push(Math.max(minAlt, Math.min(maxAlt, baseAlt + variation)));
    } else if (elevLoss > elevGain * 1.5) {
      // Predominantly downhill
      const baseAlt = maxAlt - (maxAlt - minAlt) * Math.pow(progress, 0.6);
      const variation = undulation * altRange * 0.15 * Math.sin(r * Math.PI * 2);
      waypoints.push(Math.max(minAlt, Math.min(maxAlt, baseAlt + variation)));
    } else {
      // Mixed terrain - create peaks and valleys
      const peakPhase = Math.sin(progress * Math.PI * (1 + undulation * 2));
      const baseAlt = minAlt + (maxAlt - minAlt) * (0.5 + 0.5 * peakPhase);
      const variation = undulation * altRange * 0.2 * (r - 0.5);
      waypoints.push(Math.max(minAlt, Math.min(maxAlt, baseAlt + variation)));
    }
  }
  
  // Ensure we hit max altitude somewhere
  const maxIdx = waypoints.reduce((best, val, idx) => val > waypoints[best] ? idx : best, 0);
  waypoints[maxIdx] = maxAlt;
  
  // Ensure we hit min altitude somewhere
  const minIdx = waypoints.reduce((best, val, idx) => val < waypoints[best] ? idx : best, 0);
  waypoints[minIdx] = minAlt;

  // Interpolate between waypoints with cubic smoothing
  const allPoints = [waypoints[0]];
  for (let w = 0; w < waypoints.length - 1; w++) {
    const from = waypoints[w];
    const to = waypoints[w + 1];
    const segSteps = Math.round(numSegments / waypoints.length);
    for (let s = 1; s <= segSteps; s++) {
      const t = s / segSteps;
      // Smooth step interpolation
      const smooth = t * t * (3 - 2 * t);
      const baseAlt = from + (to - from) * smooth;
      // Add small terrain noise
      const noise = altRange * 0.03 * Math.sin(pseudoRandom(w * 100 + s) * Math.PI * 4) * (1 - Math.abs(t - 0.5) * 2);
      allPoints.push(Math.max(minAlt, Math.min(maxAlt, baseAlt + noise)));
    }
  }

  // Convert to SVG points
  const svgPoints = allPoints.map((alt, i) => {
    const x = padding.left + (i / (allPoints.length - 1)) * chartW;
    const y = padding.top + chartH - ((alt - minAlt) / altRange * chartH);
    return { x, y, alt };
  });

  // Build SVG path
  const linePath = svgPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const areaPath = linePath + ` L ${svgPoints[svgPoints.length - 1].x.toFixed(1)} ${padding.top + chartH} L ${padding.left} ${padding.top + chartH} Z`;

  // Y-axis: 3-4 labels evenly spaced
  const numYLabels = altRange > 500 ? 4 : 3;
  const yLabels = [];
  for (let i = 0; i < numYLabels; i++) {
    const alt = minAlt + (altRange / (numYLabels - 1)) * i;
    yLabels.push({
      alt: Math.round(alt),
      y: padding.top + chartH - ((alt - minAlt) / altRange * chartH),
    });
  }

  const distLabel = distance >= 1000 ? `${(distance / 1000).toFixed(1)} km` : `${Math.round(distance)} m`;

  return (
    <div data-testid="elevation-profile">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        {/* Grid lines */}
        {yLabels.map((l, i) => (
          <g key={i}>
            <line x1={padding.left} y1={l.y} x2={width - padding.right} y2={l.y} stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="3,3" />
            <text x={padding.left - 4} y={l.y + 3} fontSize="7" fill="#9CA3AF" textAnchor="end">{l.alt}m</text>
          </g>
        ))}

        {/* Gradient fill */}
        <defs>
          <linearGradient id="elevGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#38B2AC" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#38B2AC" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#elevGrad)" />

        {/* Profile line */}
        <path d={linePath} fill="none" stroke="#319795" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />

        {/* Start/End markers */}
        <circle cx={svgPoints[0].x} cy={svgPoints[0].y} r="3" fill="#319795" stroke="white" strokeWidth="1" />
        <circle cx={svgPoints[svgPoints.length - 1].x} cy={svgPoints[svgPoints.length - 1].y} r="3" fill="#EF4444" stroke="white" strokeWidth="1" />

        {/* X-axis labels */}
        <text x={padding.left} y={height - 4} fontSize="7" fill="#9CA3AF">0</text>
        <text x={width - padding.right} y={height - 4} fontSize="7" fill="#9CA3AF" textAnchor="end">{distLabel}</text>
        <text x={padding.left + chartW * 0.35} y={height - 4} fontSize="7" fill="#319795" textAnchor="middle">+{Math.round(elevGain)}m</text>
        <text x={padding.left + chartW * 0.65} y={height - 4} fontSize="7" fill="#EF4444" textAnchor="middle">-{Math.round(elevLoss)}m</text>
      </svg>
    </div>
  );
};

export default ElevationProfile;
